import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography, IconButton } from "@mui/material";
import AlertsList from "./AlertsList";
import DismissiedAlertsList from "./AlertsDimissedList";
import { useCreateExceptions } from "../../../../../../../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { Alert } from "../../../../../../../../../api/homeSiteAlerts/types";
import { useUpdateAlerts } from "../../../../../../../../../reactQuery/homeSites/alerts/homesiteAlerts";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import {
  ALERT_ACTIVE_STATUS,
  ALERT_AUTORESOLVED_STATUS,
  ALERT_RESOLVED_STATUS,
  ALL_MILESTONES,
  STANDARD_HEIGHT_OF_DROPDOWN,
} from "../../../../../../../../../utils/constants";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DivisionMileStone } from "../../../../../../../../../api/divisionMileStones/types";
import { thinDarkScrollbar } from "../../../../../../../../../utils/scrollbarStyles";

interface AlertsComponentProps {
  userId: string;
  homeSiteId: string;
  alertState: Alert[];
  mileStones: DivisionMileStone[];
  divisionCode: string;
  conciergeEmail: string;
  milestoneFilter?: string;
  selectedMilestone: string; 
  setAlertMilestone: (m: string) => void; 
  showSnackbar: (
    message: string,
    severity: "success" | "error" | "info"
  ) => void;
}

const AlertsComponent: React.FC<AlertsComponentProps> = ({
  alertState,
  homeSiteId,
  userId,
  mileStones,
  divisionCode,
  conciergeEmail,
  showSnackbar,
  milestoneFilter,
  selectedMilestone,
  setAlertMilestone,
}) => {
  const { mutate: updateAlert } = useUpdateAlerts(divisionCode, homeSiteId);
  const { mutate: createException } = useCreateExceptions(divisionCode, homeSiteId);
  const [showDimissed, setShowDismissed] = useState(false);
  const [activePage, setActivePage] = useState(1);

  // Helper function to get valid milestone value for this component
  const getValidMilestoneValue = useCallback(
    (milestone: string) => {
      if (milestone && mileStones.some(ms => ms.mileStoneName === milestone)) {
        return milestone;
      }
      return ALL_MILESTONES;
    },
    [mileStones]
  );

  const effectiveMilestone = useMemo(
    () => getValidMilestoneValue(selectedMilestone),
    [selectedMilestone, getValidMilestoneValue]
  );

  const ITEMS_PER_PAGE = 5;

  const milestoneCounts = alertState
    .filter((als) => {
      if (showDimissed) {
        return als.status === ALERT_RESOLVED_STATUS || als.status === ALERT_AUTORESOLVED_STATUS;
      } else {
        return als.status === ALERT_ACTIVE_STATUS;
      }
    })
    .reduce((acc, note) => {
      acc[note.mileStoneName] = (acc[note.mileStoneName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const handleMilestoneChange = (event: SelectChangeEvent<string>) => {
    const raw = event.target.value as string;
    const val = getValidMilestoneValue(raw);
    setAlertMilestone(val);
    setActivePage(1);
  };

  const handleAddClick = () => setShowDismissed(true);

  const activeAlerts = alertState.filter(
    (alert) =>
      (effectiveMilestone === ALL_MILESTONES || alert.mileStoneName === effectiveMilestone) && alert.status === ALERT_ACTIVE_STATUS
  );

  const dismissedAlerts = alertState.filter(
    (alert) =>
      (effectiveMilestone === ALL_MILESTONES || alert.mileStoneName === effectiveMilestone) &&
      (alert.status === ALERT_RESOLVED_STATUS || alert.status === ALERT_AUTORESOLVED_STATUS)
  );

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        width: "504px",
        height: "515px",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        padding: 2,
        boxSizing: "border-box",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        "-ms-overflow-style": "none",
        "scrollbar-width": "none",
      }}
    >
      {showDimissed && (
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => setShowDismissed(false)}>
            <ArrowBackIcon sx={{ color: "#1C2B36" }} />
          </IconButton>
          <Typography sx={{ fontWeight: "bold", fontSize: 16 }}> Dismissed Alerts
          </Typography>
        </Box>
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
        <FormControl variant="standard" style={{ flexGrow: 1, marginRight: "16px" }}>
          <Select
            IconComponent={(props) => (
              <GridExpandMoreIcon
                {...props}
                sx={{
                  fontSize: 25,
                }}
              />
            )}
            labelId="milestone-select-label"
            value={effectiveMilestone}
            onChange={handleMilestoneChange}
            label="Milestone"
            sx={{
              fontWeight: "400",
              "& .MuiSelect-icon": {
                color: "#3E5463",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: STANDARD_HEIGHT_OF_DROPDOWN,
                  overflowY: "auto",
                  ...thinDarkScrollbar,
                },
              },
            }}
          >
            <MenuItem value={ALL_MILESTONES}>
              All Milestones (
              <strong>
                {
                  alertState.filter((el) => {
                    if (showDimissed) {
                      return el.status === ALERT_RESOLVED_STATUS || el.status === ALERT_AUTORESOLVED_STATUS;
                    } else {
                      return el.status === ALERT_ACTIVE_STATUS;
                    }
                  }).length
                }
              </strong>
              )
            </MenuItem>
            {mileStones.map((milestone) => (
              <MenuItem key={milestone.mileStoneName} value={milestone.mileStoneName}>
                {milestone.mileStoneName} (<strong>{milestoneCounts[milestone.mileStoneName] || 0}</strong>)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {!showDimissed ? (
        <>
          <AlertsList
            alerts={activeAlerts}
            page={activePage}
            itemsPerPage={ITEMS_PER_PAGE}
            updateAlert={updateAlert}
            createException={createException}
            setActivePage={setActivePage}
            homeSiteId={homeSiteId}
            userId={userId}
            title={""}
            mileStoneName={effectiveMilestone}
            divisionCode={divisionCode}
            conciergeEmail={conciergeEmail}
            showSnackbar={showSnackbar}
          />

          <Box display="flex" justifyContent="center" alignItems="center" marginTop={2}>
            {dismissedAlerts.length > 0 && (
              <Typography
                variant="h4"
                sx={{
                  color: "#005DAA",
                  fontWeight: "700",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
                onClick={handleAddClick}
              >
                Show Dismissed ({dismissedAlerts.length})
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <DismissiedAlertsList dimissedAlerts={dismissedAlerts} itemsPerPage={ITEMS_PER_PAGE} />
      )}
    </Box>
  );
};

export default AlertsComponent;
